import m from 'mithril'
import { css } from '@emotion/css'
import { colors, font, space, card, input, btnPrimary, btnSecondary } from '../styles/theme.js'

// --- Styles ---

const smallBtn = css`
  ${btnSecondary};
  font-size: ${font.size.xs};
  padding: ${space.xs} ${space.sm};
`

const sectionTitle = css`
  font-size: ${font.size.sm};
  font-weight: ${font.weight.semibold};
  color: ${colors.textSecondary};
  margin-bottom: ${space.sm};
  margin-top: ${space.lg};
  &:first-child { margin-top: 0; }
`

const chipWrap = css`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`

const chip = css`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: ${font.size.xs};
  cursor: pointer;
  border: 1px solid ${colors.border};
  background: ${colors.bgCard};
  user-select: none;
  transition: background 0.1s, border-color 0.1s;
  &:hover { border-color: ${colors.brand}; }
`

const chipActive = css`
  ${chip};
  background: ${colors.brand};
  border-color: ${colors.brand};
  color: white;
`

const chipSmall = css`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: ${font.size.xs};
  background: ${colors.bgSidebar};
  border: 1px solid ${colors.border};
`

const detailGroup = css`
  margin-bottom: ${space.sm};
`

const detailGroupLabel = css`
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
  color: ${colors.textSecondary};
  margin-bottom: 2px;
`

const inlineRow = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
  margin-bottom: ${space.sm};
`

const smallInput = css`
  ${input};
  width: 60px;
  font-size: ${font.size.sm};
  padding: ${space.xs} ${space.sm};
  text-align: center;
`

const countInput = css`
  ${input};
  width: 80px;
  font-size: ${font.size.sm};
  padding: ${space.xs} ${space.sm};
`

const generateRow = css`
  display: flex;
  align-items: center;
  gap: ${space.md};
  margin-top: ${space.lg};
`

const genCard = css`
  ${card};
  margin-bottom: ${space.sm};
  position: relative;
`

const genContent = css`
  font-size: ${font.size.sm};
  white-space: pre-wrap;
  margin-bottom: ${space.sm};
`

const genMeta = css`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: ${space.sm};
`

const genActions = css`
  display: flex;
  gap: 4px;
  align-items: center;
`

const editedBadge = css`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: ${font.size.xs};
  background: ${colors.yellow};
  border: 1px solid ${colors.border};
  color: ${colors.text};
`

const bulkBar = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
  margin-bottom: ${space.md};
  padding: ${space.sm} ${space.md};
  background: ${colors.bgSidebar};
  border-radius: 6px;
`

const emptyText = css`
  font-size: ${font.size.sm};
  color: ${colors.textMuted};
  padding: ${space.md};
  text-align: center;
`

const progressBar = css`
  display: flex;
  align-items: center;
  gap: ${space.sm};
  margin-top: ${space.sm};
  font-size: ${font.size.sm};
  color: ${colors.textSecondary};
`

const progressTrack = css`
  flex: 1;
  height: 6px;
  background: ${colors.border};
  border-radius: 3px;
  overflow: hidden;
`

const progressFill = css`
  height: 100%;
  background: ${colors.brand};
  border-radius: 3px;
  transition: width 0.3s ease;
`

const checkboxLabel = css`
  display: flex;
  align-items: center;
  gap: ${space.xs};
  font-size: ${font.size.sm};
  cursor: pointer;
`

const filterGroup = css`
  margin-bottom: ${space.md};
`

const filterLabel = css`
  display: block;
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
  color: ${colors.textSecondary};
  margin-bottom: ${space.xs};
`

const moreRow = css`
  display: flex;
  align-items: center;
  gap: ${space.xs};
  margin-top: ${space.xs};
`

const rubricSection = css`
  margin-top: ${space.sm};
  padding: ${space.sm} ${space.md};
  background: ${colors.bgSidebar};
  border-left: 3px solid ${colors.brand};
  border-radius: 0 6px 6px 0;
`

const critRow = css`
  display: flex;
  align-items: flex-start;
  gap: ${space.sm};
  padding: ${space.xs} 0;
  font-size: ${font.size.sm};
  &:not(:last-child) { border-bottom: 1px solid ${colors.border}; }
`

const critBadge = css`
  display: inline-block;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: ${font.size.xs};
  font-weight: ${font.weight.medium};
  white-space: nowrap;
  flex-shrink: 0;
`

const expectedAnswer = css`
  font-size: ${font.size.xs};
  color: ${colors.textMuted};
  margin-top: 2px;
`

function critBadgeStyle(criticality) {
  switch (criticality) {
    case 'critical': return `background: ${colors.critical}; color: white;`
    case 'important': return `background: ${colors.orange}; color: white;`
    case 'info': return `background: ${colors.border}; color: ${colors.text};`
    default: return ''
  }
}

function filterCriteriaForGeneration(criteria, detailValues, details) {
  if (!criteria || criteria.length === 0) return []
  if (!detailValues || Object.keys(detailValues).length === 0) return criteria

  // Normalize snapshot values: older generations stored labels, newer ones store IDs.
  // Build a resolved map that always uses option IDs.
  const resolved = {}
  for (const [detailId, value] of Object.entries(detailValues)) {
    const detail = details && details.find(d => d.id === detailId)
    if (detail && detail.options) {
      // Check if value is already an option ID
      const byId = detail.options.find(o => (typeof o === 'string' ? o : o.id) === value)
      if (byId) {
        resolved[detailId] = typeof byId === 'string' ? byId : byId.id
      } else {
        // Try matching by label (legacy snapshots)
        const byLabel = detail.options.find(o => typeof o !== 'string' && o.label === value)
        resolved[detailId] = byLabel ? byLabel.id : value
      }
    } else {
      resolved[detailId] = value
    }
  }

  return criteria.filter(c => {
    const scopes = c.scopes || []
    if (scopes.length === 0) return true
    const groups = new Map()
    for (const s of scopes) {
      if (!groups.has(s.detail_id)) groups.set(s.detail_id, [])
      groups.get(s.detail_id).push(s.detail_value)
    }
    for (const [detailId, allowed] of groups) {
      if (!resolved[detailId] || !allowed.includes(resolved[detailId])) return false
    }
    return true
  })
}

/**
 * GenerationPanel component
 *
 * attrs:
 *   generations: [{ id, content, config_snapshot, edited, created_at }]
 *   generationConfig: object or null
 *   details: array of pack details
 *   criteria: array of scenario criteria with scopes
 *   stylePresets: array
 *   projectId, packId, scenarioId: route IDs for Remix navigation
 *   onGenerate: (data) => Promise
 *   onDeleteGeneration: (id) => Promise
 *   onGenerateMore: (id, data) => Promise
 *   onExport: (data) => Promise
 *   saving: boolean
 *   generating: boolean
 *   generationProgress: { completed, total } | null
 */
export function GenerationPanel() {
  // Generation params
  let allCombinations = true
  let selectedDetailOptions = {}  // { detailId: Set of optionIds }
  let selectedPresetIds = new Set()
  let noiseMax = 2
  let count = 5

  // Results state
  let selectedGenIds = new Set()
  let moreGenId = null
  let moreCount = 3
  let rubricGenId = null

  return {
    view(vnode) {
      const {
        generations = [],
        generationConfig,
        details = [],
        criteria = [],
        stylePresets = [],
        projectId,
        packId,
        scenarioId,
        onGenerate,
        onDeleteGeneration,
        onGenerateMore,
        onExport,
        saving,
        generating,
        generationProgress,
      } = vnode.attrs

      const children = []

      // --- Generate batch ---
      children.push(m('h3', { key: 'params-title', class: sectionTitle }, 'Generate batch of scenarios'))

      if (!generationConfig) {
        children.push(m('div', { key: 'no-config', class: emptyText },
          'LLM not configured. Ask the project owner to set it up in Settings.'
        ))
      }

      // Detail combinations
      if (details.length > 0) {
        const detailItems = []
        detailItems.push(m('label', { class: checkboxLabel }, [
          m('input', {
            type: 'checkbox',
            checked: allCombinations,
            onchange: () => { allCombinations = !allCombinations },
          }),
          'All details',
        ]))
        if (!allCombinations) {
          for (const d of details) {
            const opts = []
            if (d.type === 'enum' && d.options) {
              for (const o of d.options) {
                opts.push(typeof o === 'string' ? { id: o, label: o } : { id: o.id, label: o.label || o.id })
              }
            } else if (d.type === 'boolean') {
              opts.push({ id: 'true', label: 'Yes' }, { id: 'false', label: 'No' })
            }
            if (opts.length === 0) continue

            const selected = selectedDetailOptions[d.id] || new Set(opts.map(o => o.id))
            const allSelected = selected.size === opts.length

            const groupChildren = []
            groupChildren.push(m('div', { key: 'label', class: detailGroupLabel }, d.name))
            groupChildren.push(m('div', { key: 'chips', class: chipWrap },
              opts.map(o =>
                m('span', {
                  key: o.id,
                  class: selected.has(o.id) ? chipActive : chip,
                  onclick: () => {
                    const s = new Set(selected)
                    if (allSelected) {
                      // First click when all selected: select only this one
                      selectedDetailOptions = { ...selectedDetailOptions, [d.id]: new Set([o.id]) }
                    } else if (s.has(o.id) && s.size === 1) {
                      // Last one: reselect all
                      selectedDetailOptions = { ...selectedDetailOptions, [d.id]: new Set(opts.map(x => x.id)) }
                    } else {
                      if (s.has(o.id)) s.delete(o.id); else s.add(o.id)
                      selectedDetailOptions = { ...selectedDetailOptions, [d.id]: s }
                    }
                  },
                }, o.label)
              )
            ))
            detailItems.push(m('div', { key: `detail-${d.id}`, class: detailGroup }, groupChildren))
          }
        }
        children.push(m('div', { key: 'detail-combos', class: filterGroup }, detailItems))
      }

      // Style presets
      if (stylePresets.length > 0) {
        const allPresetsSelected = selectedPresetIds.size === 0
        const presetItems = []
        presetItems.push(m('label', { class: checkboxLabel }, [
          m('input', {
            type: 'checkbox',
            checked: allPresetsSelected,
            onchange: () => { selectedPresetIds = allPresetsSelected ? new Set([stylePresets[0].id]) : new Set() },
          }),
          'All style presets',
        ]))
        if (!allPresetsSelected) {
          presetItems.push(m('div', { class: chipWrap, style: `margin-top: ${space.xs};` },
            stylePresets.map(p =>
              m('span', {
                key: p.id,
                class: selectedPresetIds.has(p.id) ? chipActive : chip,
                onclick: () => {
                  const s = new Set(selectedPresetIds)
                  if (s.has(p.id)) { s.delete(p.id) } else { s.add(p.id) }
                  selectedPresetIds = s
                },
              }, p.label)
            )
          ))
        }
        children.push(m('div', { key: 'style-presets', class: filterGroup }, presetItems))
      }

      // Noise range
      children.push(m('div', { key: 'noise-range', class: inlineRow }, [
        m('span', { style: `font-size: ${font.size.sm}; color: ${colors.textSecondary};` }, 'Maximum noise snippets:'),
        m('input', {
          class: smallInput,
          type: 'number',
          min: 0,
          value: noiseMax,
          oninput: (e) => { noiseMax = Math.max(0, parseInt(e.target.value) || 0) },
        }),
      ]))

      // Generate row
      children.push(m('div', { key: 'generate-row', class: generateRow }, [
        m('span', { style: `font-size: ${font.size.sm}; color: ${colors.textSecondary};` }, 'Generate'),
        m('input', {
          class: countInput,
          type: 'number',
          min: 1,
          value: count,
          oninput: (e) => { count = Math.max(1, parseInt(e.target.value) || 1) },
        }),
        m('span', { style: `font-size: ${font.size.sm}; color: ${colors.textSecondary};` }, count === 1 ? 'scenario.' : 'scenarios.'),
        m('button', {
          class: btnPrimary,
          disabled: !generationConfig || count < 1 || generating,
          onclick: () => {
            const data = { count, noiseSnippetRange: { min: 0, max: noiseMax } }
            if (!allCombinations) {
              const filter = {}
              for (const d of details) {
                const opts = selectedDetailOptions[d.id]
                if (opts && opts.size > 0) {
                  // Only include filter if not all options are selected
                  const allOpts = d.type === 'boolean' ? ['true', 'false']
                    : (d.options || []).map(o => typeof o === 'string' ? o : o.id)
                  if (opts.size < allOpts.length) {
                    filter[d.id] = [...opts]
                  }
                }
              }
              if (Object.keys(filter).length > 0) {
                data.detailValueFilter = filter
              }
            }
            if (selectedPresetIds.size > 0) {
              data.stylePresetIds = [...selectedPresetIds]
            }
            onGenerate(data)
          },
        }, generating
          ? (generationProgress ? `Generating ${generationProgress.completed}/${generationProgress.total}...` : 'Generating...')
          : 'Generate'),
      ]))

      // Progress bar
      if (generating && generationProgress && generationProgress.total > 0) {
        const pct = Math.round((generationProgress.completed / generationProgress.total) * 100)
        children.push(m('div', { key: 'progress-bar', class: progressBar }, [
          m('div', { class: progressTrack },
            m('div', { class: progressFill, style: `width: ${pct}%;` })
          ),
          m('span', `${generationProgress.completed} of ${generationProgress.total}`),
        ]))
      }

      // --- Results ---
      if (generations.length > 0) {
        children.push(m('h3', { key: 'results-title', class: sectionTitle }, `Generations (${generations.length})`))

        // Bulk bar
        const hasSelected = selectedGenIds.size > 0
        children.push(m('div', { key: 'bulk-bar', class: bulkBar }, [
          m('label', { class: checkboxLabel }, [
            m('input', {
              type: 'checkbox',
              checked: selectedGenIds.size === generations.length && generations.length > 0,
              onchange: () => {
                if (selectedGenIds.size === generations.length) {
                  selectedGenIds = new Set()
                } else {
                  selectedGenIds = new Set(generations.map(g => g.id))
                }
              },
            }),
            m('span', { style: `font-size: ${font.size.xs};` }, `${selectedGenIds.size} selected`),
          ]),
          hasSelected ? m('button', {
            class: smallBtn,
            disabled: saving,
            onclick: async () => {
              for (const id of selectedGenIds) {
                await onDeleteGeneration(id)
              }
              selectedGenIds = new Set()
            },
          }, 'Delete Selected') : null,
          m('button', {
            class: smallBtn,
            onclick: () => {
              const ids = hasSelected ? [...selectedGenIds] : undefined
              onExport({ ids, format: 'json' })
            },
          }, hasSelected ? 'Export Selected' : 'Export All'),
        ].filter(Boolean)))

        // Generation cards
        for (const gen of generations) {
          const isMore = moreGenId === gen.id
          let snapshot = {}
          try { snapshot = gen.config_snapshot ? JSON.parse(gen.config_snapshot) : {} } catch { snapshot = {} }

          const dvObj = snapshot.detailValues
            ? (typeof snapshot.detailValues === 'string' ? JSON.parse(snapshot.detailValues) : snapshot.detailValues)
            : {}
          const filteredCriteria = filterCriteriaForGeneration(criteria, dvObj, details)

          {
            const metaItems = []
            if (snapshot.detailValues) {
              for (const [detailId, value] of Object.entries(dvObj || {})) {
                const detail = details.find(d => d.id === detailId)
                const detailName = detail ? detail.name : detailId
                let valueLabel = value
                if (value === '_unknown') {
                  valueLabel = 'Unknown'
                } else if (detail && detail.options) {
                  const opt = detail.options.find(o => (typeof o === 'string' ? o : o.id) === value)
                  if (opt) valueLabel = typeof opt === 'string' ? opt : (opt.label || opt.id)
                }
                metaItems.push(m('span', { key: `dv-${detailId}`, class: chipSmall }, `${detailName}: ${valueLabel}`))
              }
            }
            if (snapshot.stylePresetId) {
              const preset = stylePresets.find(p => p.id === snapshot.stylePresetId)
              metaItems.push(m('span', { key: 'style', class: chipSmall }, preset ? preset.label : snapshot.stylePresetId))
            }

            children.push(m('div', { key: gen.id, class: genCard }, [
              m('div', { style: 'display: flex; align-items: flex-start; gap: 8px;' }, [
                m('input', {
                  type: 'checkbox',
                  checked: selectedGenIds.has(gen.id),
                  onchange: () => {
                    const s = new Set(selectedGenIds)
                    if (s.has(gen.id)) { s.delete(gen.id) } else { s.add(gen.id) }
                    selectedGenIds = s
                  },
                }),
                m('div', { style: 'flex: 1; min-width: 0;' }, [
                  m('div', { class: genContent }, gen.content),
                  metaItems.length > 0 ? m('div', { class: genMeta }, metaItems) : null,
                  gen.edited ? m('span', { class: editedBadge }, 'edited') : null,
                ]),
              ]),
              m('div', { class: genActions }, [
                m('button', {
                  class: smallBtn,
                  onclick: () => {
                    m.route.set(`/projects/${projectId}/scenarios/${packId}/${scenarioId}/remix/${gen.id}`)
                  },
                }, 'Remix'),
                m('button', {
                  class: smallBtn,
                  disabled: saving,
                  onclick: () => onDeleteGeneration(gen.id),
                }, 'Delete'),
                m('button', {
                  class: smallBtn,
                  onclick: () => { moreGenId = moreGenId === gen.id ? null : gen.id; moreCount = 3 },
                }, 'More like this'),
                criteria.length > 0 ? m('button', {
                  class: smallBtn,
                  onclick: () => { rubricGenId = rubricGenId === gen.id ? null : gen.id },
                }, `Rubric (${filteredCriteria.length})`) : null,
              ].filter(Boolean)),
              isMore ? m('div', { class: moreRow }, [
                m('input', {
                  class: smallInput,
                  type: 'number',
                  min: 1,
                  value: moreCount,
                  oninput: (e) => { moreCount = Math.max(1, parseInt(e.target.value) || 1) },
                }),
                m('button', {
                  class: css`${btnPrimary}; font-size: ${font.size.xs}; padding: ${space.xs} ${space.sm};`,
                  disabled: generating,
                  onclick: async () => {
                    await onGenerateMore(gen.id, { count: moreCount })
                    moreGenId = null
                  },
                }, generating
                ? (generationProgress ? `${generationProgress.completed}/${generationProgress.total}...` : 'Generating...')
                : 'Go'),
              ]) : null,
              rubricGenId === gen.id && filteredCriteria.length > 0
                ? m('div', { class: rubricSection }, filteredCriteria.map(c =>
                    m('div', { key: c.id, class: critRow }, [
                      m('span', { class: critBadge, style: critBadgeStyle(c.criticality) }, c.criticality),
                      m('div', { style: 'flex: 1; min-width: 0;' }, [
                        m('div', c.description),
                        c.expected_answer ? m('div', { class: expectedAnswer }, 'Expected: ' + c.expected_answer) : null,
                      ]),
                    ])
                  ))
                : null,
            ]))
          }
        }
      } else {
        children.push(m('div', { key: 'results-empty', class: emptyText }, 'No generations yet. Configure parameters above and click Generate.'))
      }

      return m('div', children)
    },
  }
}
